'use client';

import {
  boolCheckboxSpec,
  dataDependencyIdsAdder,
  dataDependencyTypePicker,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '~/app/lib/editing-configs';
import set from 'lodash/set';
import AuthenticationWrapped from '../authentication/authentication';
import UniversalText from '../universal-text/universal-text';

interface PageProps {
  children: JSX.Element;
  requiresAuth: boolean;
  successUrl: string;
  loginPageCopyOverride: string;
  authRoleRequired: string;
  useBrandedLogin: boolean;
  brandedBackgroundImage: string;
  brandedIcon: string;
  brandedTitle: string;
  brandedSubtitle: string;
  customCtaText: string;
  topLoginBoxColor: string;
  bottomLoginBoxColor: string;
  topLoginBoxMargin: number;
  bottomLoginBoxMargin: number;
  outputAnimatedArrowAboveCta: boolean;
  animatedImageUrl: string;
  loginPageTitle: object;
  loginPageSubtitle: object;
}

const Page = ({
  children,
  requiresAuth,
  successUrl,
  loginPageCopyOverride,
  authRoleRequired,
  useBrandedLogin,
  brandedBackgroundImage,
  brandedIcon,
  brandedTitle,
  brandedSubtitle,
  customCtaText,
  topLoginBoxColor,
  bottomLoginBoxColor,
  topLoginBoxMargin,
  bottomLoginBoxMargin,
  outputAnimatedArrowAboveCta,
  animatedImageUrl,
  loginPageTitle,
  loginPageSubtitle,
}: PageProps) => {
  return (
    <div>
      <AuthenticationWrapped
        requiresAuth={requiresAuth}
        authRoleRequired={authRoleRequired}
        loginPageCopyOverride={loginPageCopyOverride}
        onSuccess={successUrl}
        // branded Login
        useBrandedLogin={useBrandedLogin}
        brandedBackgroundImage={brandedBackgroundImage}
        brandedIcon={brandedIcon}
        brandedTitle={brandedTitle}
        brandedSubtitle={brandedSubtitle}
        customCtaText={customCtaText}
        topLoginBoxColor={topLoginBoxColor}
        bottomLoginBoxColor={bottomLoginBoxColor}
        topLoginBoxMargin={Number(topLoginBoxMargin)}
        bottomLoginBoxMargin={Number(bottomLoginBoxMargin)}
        outputAnimatedArrowAboveCta={outputAnimatedArrowAboveCta}
        animatedImageUrl={animatedImageUrl}
        loginPageTitle={loginPageTitle}
        loginPageSubtitle={loginPageSubtitle}
      >
        {children}
      </AuthenticationWrapped>
    </div>
  );
};

Page.displayName = 'Page';
Page.isContainer = true;

Page.getEditingConfig = (componentDocument: any) => {
  const authRoleContent =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec(
            'authRoleRequired',
            'Role required for authentication',
            {
              help: 'The role that is required for authentication. Please note: if you choose to require authentication for a page, this defaults to "nerdwallet/logged_in", meaning the page will be configured to require users to login to view the page\'s contents. If the page requires a more targeted role for authentication, please specify here. Also, please note that all pages that require authentication can be viewed by NerdWallet employees (eg: users who possess the "nerdwallet/employee" role).',
            }
          ),
          stringInputSpec(
            'loginPageCopyOverride',
            'Login splash page custom copy',
            {
              help: "If you'd like to output custom copy on the login splash page, please locate your custom output in this field.",
            }
          ),
          stringInputSpec('loginModalCopyOverride', 'Custom login modal copy', {
            help: "If you'd like to output custom copy in the login modal, please locate your custom output in this field.",
          }),
        ];

  const themedHeaderConfig = componentDocument?.meta?.needsGlobalNav
    ? [
        stringSelectSpec(
          'meta.theme',
          'Select the NerdWallet nav type',
          [
            { label: 'Default', value: 'default' },
            { label: 'Company', value: 'company' },
          ],
          {
            isProp: false,
            help: 'Note: requires a save to take effect.',
          }
        ),
      ]
    : [];

  const brandedLoginCustomBorderColors =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customBorderColors
      ? [
          stringInputSpec('topLoginBoxColor', 'Top Login Box Color', {
            help: 'HexColor',
          }),
          stringInputSpec('bottomLoginBoxColor', 'Bottom Login Box Color', {
            help: 'HexColor',
          }),
        ]
      : [];

  const brandedLoginCustomMargins =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customMargins
      ? [
          numberInputSpec('topLoginBoxMargin', 'Top Login Box Margin'),
          numberInputSpec('bottomLoginBoxMargin', 'Bottom Login BoxMargin'),
        ]
      : [];

  const animatedArrowInputConfig = componentDocument?.props
    ?.outputAnimatedArrowAboveCta
    ? [stringInputSpec('animatedImageUrl', 'Animated image URL')]
    : [];

  const brandedLoginInputOptions = componentDocument?.props?.useBrandedLogin
    ? [
        boolCheckboxSpec('useBrandedLogin', 'Use Branded Login'),
        stringInputSpec('brandedBackgroundImage', 'Branded Background Image'),
        stringInputSpec('brandedIcon', 'Branded Icon'),
        stringInputSpec('brandedSubtitle', 'Branded Subtitle'),
        boolCheckboxSpec(
          'outputAnimatedArrowAboveCta',
          'Output animated arrow above CTA?'
        ),
        ...animatedArrowInputConfig,
        stringInputSpec('customCtaText', 'Custom CTA Text'),
        boolCheckboxSpec('customBorderColors', 'Custom Border Colors'),
        ...brandedLoginCustomBorderColors,
        boolCheckboxSpec('customMargins', 'Custom Margin'),
        ...brandedLoginCustomMargins,
        ...UniversalText.getEditingConfig(
          componentDocument,
          'Title',
          [
            'fontWeight',
            'groupButton',
            'margin',
            'padding',
            'fontFamily',
            'textColor',
            'textSize',
            'textType',
          ],
          'loginPageTitle'
        ),
        ...UniversalText.getEditingConfig(
          componentDocument,
          'Subtitle',
          [
            'fontWeight',
            'groupButton',
            'margin',
            'padding',
            'fontFamily',
            'textColor',
            'textSize',
            'textType',
          ],
          'loginPageSubtitle',
          { text: { textType: 'text' }, disableGroup: false }
        ),
      ]
    : [boolCheckboxSpec('useBrandedLogin', 'Use Branded Login')];
  const requiresAuthInputConfig = componentDocument?.props?.requiresAuth
    ? [
        stringInputSpec('successUrl', 'Success URL', {
          help: 'URL to be redirected after SignIn/SignUp succeeded!',
        }),
        group('Branded Login', [...brandedLoginInputOptions]),
      ]
    : [];
  const customGtmContainersConfig = componentDocument?.meta
    ?.useCustomGtmContainers
    ? [
        repeatable(
          'meta.customGtmContainers',
          'Custom GTM Containers',
          'Custom GTM Container',
          () => [stringInputSpec('containerId', 'Container ID')],
          {
            isProp: false,
          }
        ),
      ]
    : [];

  const nlOpts = componentDocument?.meta?.nlOpts;
  const nlOptsInputConfig = nlOpts
    ? [
        group('NL Opts', [
          stringInputSpec('meta.nlOpts.canonicalURL', 'Canonical URL', {
            isProp: false,
          }),
          stringInputSpec('meta.nlOpts.title', 'Title', { isProp: false }),
          stringInputSpec('meta.nlOpts.description', 'Description', {
            isProp: false,
          }),
          stringInputSpec('meta.nlOpts.ogTags.title', 'OG Title', {
            isProp: false,
          }),
          stringSelectSpec(
            'meta.nlOpts.robots',
            'Robots',
            [
              { label: 'None', value: '' },
              { label: 'Follow', value: 'follow' },
              { label: 'Noindex', value: 'noindex' },
              { label: 'Nofollow', value: 'nofollow' },
            ],
            {
              isProp: false,
            }
          ),
        ]),
      ]
    : [];

  const disableNlOpts = componentDocument?.meta?.disableNlOpts;
  if (disableNlOpts) {
    set(componentDocument, 'meta.nlOpts', undefined);
  }

  return [
    boolCheckboxSpec(
      'includeInactiveOrNonMonetizingProducts',
      'Include inactive or non-monetizing products on page?',
      {
        help: 'Products will appear on the page regardless of whether they are currently active on NerdWallet. Inactive products will come with messaging indicating them as such. You may need to save and refresh the page to see this change take effect.',
      }
    ),
    boolCheckboxSpec('meta.disableNlOpts', 'Disable NL Opts', {
      isProp: false,
    }),
    ...nlOptsInputConfig,
    group('Meta Data Options', [
      // Page meta info
      boolCheckboxSpec('meta.needsGlobalNav', 'Use NerdWallet nav', {
        isProp: false,
        help: 'Note: requires a save to take effect.',
      }),
      ...themedHeaderConfig,
      boolCheckboxSpec('meta.hideGlobalFooter', 'Hide NerdWallet footer', {
        isProp: false,
        help: 'Note: requires a save to take effect.',
      }),
      boolCheckboxSpec(
        'meta.useCustomGtmContainers',
        'Use Custom GTM Containers',
        {
          isProp: false,
          help: 'Note: requires a save to take effect.',
        }
      ),
      ...customGtmContainersConfig,
      stringInputSpec(
        'meta.canonicalURLOverride',
        'Canonical URL Override Link',
        { isProp: false }
      ),
    ]),
    group('Builder Query Params Options', [
      // Builder query params
      repeatable('queryParams', 'Current Query Params', 'Query param', () => [
        stringInputSpec('name', 'Query Param Name'),
        stringInputSpec('value', 'Query Param Value'),
      ]),
      boolCheckboxSpec(
        'watchQueryParams',
        'Filter components by query params?',
        {
          help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
        }
      ),
    ]),
    group('Credit Cards Options ', [
      boolCheckboxSpec(
        'includeCompareCreditsCard',
        'Add compare credit cards feature?',
        {
          help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
        }
      ),
      componentDocument?.props?.includeCompareCreditsCard &&
        stringInputSpec(
          'compareCreditsCardEmptyLabel',
          'Add label to the empty state'
        ),
    ]),
    group('Auth Options', [
      boolCheckboxSpec(
        'hideGoogleOneTapAuthentication',
        '(Compass) Hide Google one-tap (G1T) authentication?'
      ),
      boolCheckboxSpec(
        'hideNerdWalletModals',
        '(Compass) Hide NerdWallet modals?'
      ),
      boolCheckboxSpec('requiresAuth', 'Page requires user authentication', {
        help: 'In order to view page, user must be signed in and have the required role/scope',
      }),
      ...requiresAuthInputConfig,
      ...authRoleContent,
    ]),
    group('Data Dependencies Options', [
      // Data dependencies
      // This field is read from the JSON and passed to the providers on creation in client/routes/container.jsx.
      dataDependencyTypePicker,
      dataDependencyIdsAdder,
      stringInputSpec('dataDependency.data.slug', 'Slug', { isProp: false }),
    ]),
  ].filter(Boolean);
};

export default Page;
